import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRight, ArrowRight, Sun, Moon } from 'lucide-react';
import { Language } from '@/types';
import { getLocalizedPath } from '@/lib/utils';
import type { NavItem, MegaMenuSection, MegaMenuItem } from './Navbar';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  navItems: NavItem[];
  expandedMobileMenu: string | null;
  setExpandedMobileMenu: (label: string | null) => void;
  lang: Language;
  setLang: (lang: Language) => void;
  theme: 'light' | 'dark';
  toggleTheme: () => void;
  isAcademy: boolean;
}

export const MobileMenu: React.FC<MobileMenuProps> = ({
  isOpen,
  onClose,
  navItems,
  expandedMobileMenu,
  setExpandedMobileMenu,
  lang,
  setLang,
  theme,
  toggleTheme,
  isAcademy
}) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ y: '-100%' }}
          animate={{ y: 0 }}
          exit={{ y: '-100%' }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="fixed inset-0 z-[40] bg-white/95 dark:bg-[#05070A]/95 backdrop-blur-3xl xl:hidden flex flex-col pt-[72px]"
          dir={lang === 'ar' ? 'rtl' : 'ltr'}
        >
          {/* Content Container */}
          <div className="flex-1 px-6 pb-12 overflow-y-auto w-full flex flex-col justify-between">
            <div className="w-full">
              <AnimatePresence mode="wait">
                {!expandedMobileMenu ? (
                  /* MAIN LIST VIEW */
                  <motion.div
                    key="main-list"
                    initial={{ opacity: 0, x: lang === 'ar' ? 20 : -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: lang === 'ar' ? 20 : -20 }}
                    transition={{ duration: 0.3 }}
                    className="flex flex-col gap-6 pt-4"
                  >
                    {navItems.map((item) => (
                      <div key={item.path}>
                        {item.megaMenu ? (
                          <button
                            onClick={() => setExpandedMobileMenu(item.label)}
                            className={`w-full ${lang === 'ar' ? 'text-right' : 'text-left'} flex items-center justify-between text-[1.75rem] font-bold text-slate-900 dark:text-[#EDF0F1] hover:text-[#0E93A3] dark:hover:text-[#0E93A3] transition-colors`}
                          >
                            <span>{item.label}</span>
                            <ChevronRight size={22} className={`text-slate-400 dark:text-[#9AA5AC] ${lang === 'ar' ? 'rotate-180' : ''}`} />
                          </button>
                        ) : (
                          <Link
                            to={getLocalizedPath(item.path, lang)}
                            onClick={onClose}
                            className="text-[1.75rem] font-bold text-slate-900 dark:text-[#EDF0F1] hover:text-[#0E93A3] dark:hover:text-[#0E93A3] transition-colors block"
                          >
                            {item.label}
                          </Link>
                        )}
                      </div>
                    ))}
                  </motion.div>
                ) : (
                  /* SUBMENU VIEW (e.g. Products / Solutions) */
                  <motion.div
                    key="sub-list"
                    initial={{ opacity: 0, x: lang === 'ar' ? -20 : 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: lang === 'ar' ? -20 : 20 }}
                    transition={{ duration: 0.3 }}
                    className="flex flex-col pt-4"
                  >
                    <button
                      onClick={() => setExpandedMobileMenu(null)}
                      className="flex items-center gap-2 text-slate-900 dark:text-[#EDF0F1] font-bold text-base mb-6 group hover:text-[#0E93A3] transition-colors"
                    >
                      <ChevronRight size={18} className={`${lang === 'ar' ? '' : 'rotate-180'} text-slate-500 dark:text-[#9AA5AC] group-hover:text-[#0E93A3]`} />
                      {lang === 'ar' ? 'الرجوع للقائمة الرئيسية' : 'Back to Main Menu'}
                    </button>

                    <p className="text-xs font-mono font-bold text-slate-400 dark:text-[#9AA5AC] uppercase tracking-widest mb-6">
                      {expandedMobileMenu}
                    </p>

                    {/* Render Submenu Items */}
                    <div className="flex flex-col gap-8">
                      {navItems.find(i => i.label === expandedMobileMenu)?.megaMenu?.sections.map((section: MegaMenuSection, sIdx: number) => (
                        <div key={sIdx} className="space-y-4">
                          <h4 className="text-xs font-mono font-bold uppercase tracking-wider text-slate-400 dark:text-[#9AA5AC] px-1">
                            {section.title}
                          </h4>

                          <div className="flex flex-col gap-4">
                            {section.items.map((p: MegaMenuItem) => {
                              const isExternal = p.isExternal || p.path?.startsWith('http');

                              const content = (
                                <div className="flex items-center justify-between group py-1">
                                  <div className="flex flex-col">
                                    <span className="text-lg font-bold text-slate-900 dark:text-[#EDF0F1] group-hover:text-[#0E93A3] transition-colors">{p.title}</span>
                                    {p.desc && <span className="text-xs text-slate-500 dark:text-[#9AA5AC] mt-0.5">{p.desc}</span>}
                                  </div>
                                  <ArrowRight size={20} className={`text-slate-300 dark:text-[#9AA5AC]/40 group-hover:text-[#0E93A3] transition-colors flex-shrink-0 ${isExternal ? (lang === 'ar' ? 'rotate-[135deg]' : '-rotate-45') : (lang === 'ar' ? 'rotate-180' : '')}`} />
                                </div>
                              );

                              if (isExternal) {
                                return (
                                  <a key={p.id || `${p.path}-${p.title}`} href={p.path} target="_blank" rel="noopener noreferrer" onClick={onClose} className="block">
                                    {content}
                                  </a>
                                );
                              }

                              return (
                                <Link key={p.id || `${p.path}-${p.title}`} to={getLocalizedPath(p.path, lang)} onClick={onClose} className="block">
                                  {content}
                                </Link>
                              );
                            })}
                          </div>
                          {sIdx < ((navItems.find(i => i.label === expandedMobileMenu)?.megaMenu?.sections.length || 0) - 1) && (
                            <div className="w-full h-px bg-slate-200 dark:bg-[#262C31] mt-6" />
                          )}
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Mobile Actions Footer */}
            <div className="mt-12 pt-8 border-t border-slate-205 dark:border-white/10 space-y-6">
              {/* Theme Switcher */}
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  {lang === 'ar' ? 'المظهر' : 'Appearance'}
                </span>
                <div className="flex items-center gap-1.5 p-1 bg-slate-100 dark:bg-white/[0.03] border border-slate-200/50 dark:border-white/5 rounded-xl">
                  <button
                    onClick={() => { if (theme === 'dark') toggleTheme(); }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                      theme === 'light'
                        ? 'bg-white text-slate-900 shadow-[0_2px_8px_rgba(0,0,0,0.08)]'
                        : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                    }`}
                  >
                    <Sun size={12} />
                    <span>{lang === 'ar' ? 'مضيء' : 'Light'}</span>
                  </button>
                  <button
                    onClick={() => { if (theme === 'light') toggleTheme(); }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                      theme === 'dark'
                        ? 'bg-white/10 text-white shadow-sm'
                        : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                    }`}
                  >
                    <Moon size={12} />
                    <span>{lang === 'ar' ? 'داكن' : 'Dark'}</span>
                  </button>
                </div>
              </div>

              {/* Language Switcher */}
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  {lang === 'ar' ? 'اللغة' : 'Language'}
                </span>
                <div className="flex items-center gap-1.5 p-1 bg-slate-100 dark:bg-white/[0.03] border border-slate-200/50 dark:border-white/5 rounded-xl">
                  <button
                    onClick={() => { if (lang !== 'en') setLang('en'); }}
                    className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${
                      lang === 'en'
                        ? 'bg-white text-slate-900 shadow-[0_2px_8px_rgba(0,0,0,0.08)]'
                        : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                    }`}
                  >
                    EN
                  </button>
                  <button
                    onClick={() => { if (lang !== 'ar') setLang('ar'); }}
                    className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${
                      lang === 'ar'
                        ? 'bg-white dark:bg-white/10 text-slate-900 dark:text-white shadow-sm'
                        : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                    }`}
                  >
                    AR
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
